/**
 * Imperative GQL client + adapter layer for the translations resources
 * (translated questions/supplements, translated fields, translation jobs).
 *
 * This is the GQL → legacy boundary: responses are converted to the
 * snake_case shapes in `client/EditPage/V2/QuestionEditorV2Store.types`
 * that all translation consumers still expect.
 */
import client from 'apollo/client';

import type {
  AlternateContentLanguage,
  TranslatedField,
  TranslatedQuestion,
  TranslatedSupplement,
  TranslationRequest
} from 'client/EditPage/V2/QuestionEditorV2Store.types';

import type {
  CreateTranslationJobMutation,
  CreateTranslationJobMutationVariables,
  GetTranslatedQuestionQuery,
  GetTranslatedQuestionQueryVariables,
  GetTranslatedSupplementQuery,
  GetTranslatedSupplementQueryVariables,
  GetTranslationJobQuery,
  GetTranslationJobQueryVariables,
  TranslatedFieldPartsFragment,
  UpsertTranslatedQuestionFieldMutation,
  UpsertTranslatedQuestionFieldMutationVariables,
  UpsertTranslatedSupplementFieldMutation,
  UpsertTranslatedSupplementFieldMutationVariables
} from './Translations.queries.generated';
import {
  CreateTranslationJobDocument,
  GetTranslatedQuestionDocument,
  GetTranslatedSupplementDocument,
  GetTranslationJobDocument,
  UpsertTranslatedQuestionFieldDocument,
  UpsertTranslatedSupplementFieldDocument
} from './Translations.queries.generated';

export interface TranslationJobStatus {
  id: string;
  status: string;
}

interface TranslationFetchOptions {
  includeDrafts?: boolean;
  signal?: AbortSignal;
}

type GqlTranslatedQuestion = NonNullable<GetTranslatedQuestionQuery['translatedQuestion']>;
type GqlTranslatedSupplement = NonNullable<GetTranslatedSupplementQuery['translatedSupplement']>;

// The `JsonString` scalar arrives as a JSON-encoded string; the legacy
// JSON:API layer delivered these fields already decoded, so parse at the
// boundary before handing values to consumers.
const parseJsonScalar = (value: unknown): any => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const parseJsonScalarArray = (values: any[] | null | undefined): any =>
  values ? values.map(parseJsonScalar) : values;

// `context.fetchOptions` is merged into the underlying fetch call by Apollo's
// http link; this preserves the abort behavior the legacy request layer had.
const requestContext = (signal?: AbortSignal) => (signal ? {fetchOptions: {signal}} : undefined);

const toLegacyTranslatedField = (field: TranslatedFieldPartsFragment): TranslatedField =>
  ({
    id: field.id,
    field: field.field,
    text: field.text,
    notes: field.notes,
    last_modified_by: field.lastModifiedBy ?? null,
    language: field.language,
    resource_id: field.resourceId,
    status: field.status
  }) as TranslatedField;

// The legacy layer blind-cast `.toJS()` output to these interfaces, so
// nullable scalars pass through unchanged here to keep runtime parity.
const toLegacyTranslatedQuestion = (question: GqlTranslatedQuestion): TranslatedQuestion =>
  ({
    id: question.id,
    meets_translation_requirements: question.meetsTranslationRequirements,
    required_fields: question.requiredFields,
    translate: question.translate,
    translate_supplements: question.translateSupplements,
    title: question.title,
    prompt: question.prompt,
    solution_text: question.solutionText,
    dropdown_text: question.dropdownText,
    dropdowns: parseJsonScalarArray(question.dropdowns),
    inputs: parseJsonScalarArray(question.inputs),
    sample: question.sample,
    options: parseJsonScalarArray(question.options),
    uncorrected_text: question.uncorrectedText,
    snippet_prompt: question.snippetPrompt,
    highlight_prompt: question.highlightPrompt,
    header_left: question.headerLeft,
    header_right: question.headerRight,
    rows: parseJsonScalarArray(question.rows),
    translated_fields: question.translatedFields.map(toLegacyTranslatedField)
  }) as TranslatedQuestion;

const toLegacyTranslatedSupplement = (supplement: GqlTranslatedSupplement): TranslatedSupplement =>
  ({
    id: supplement.id,
    meets_translation_requirements: supplement.meetsTranslationRequirements,
    translate: supplement.translate,
    required_fields: supplement.requiredFields,
    name: supplement.name,
    content: parseJsonScalar(supplement.content),
    translated_fields: supplement.translatedFields.map(toLegacyTranslatedField)
  }) as TranslatedSupplement;

const toMutationErrorMessage = (
  errors: Array<{message?: string | null}> | undefined,
  fallback: string
): string =>
  errors
    ?.map((error) => error.message)
    .filter(Boolean)
    .join(', ') || fallback;

export const fetchTranslatedQuestionGql = async (
  questionId: string,
  language: AlternateContentLanguage,
  {includeDrafts = false, signal}: TranslationFetchOptions = {}
): Promise<TranslatedQuestion> => {
  const {data} = await client.query<GetTranslatedQuestionQuery, GetTranslatedQuestionQueryVariables>(
    {
      query: GetTranslatedQuestionDocument,
      variables: {id: questionId, language, includeDrafts},
      // Translated content is mutated outside Apollo (translation jobs, field
      // upserts) and the same id exists once per language, so cached results
      // must never be served or normalized.
      fetchPolicy: 'no-cache',
      context: requestContext(signal)
    }
  );

  const translatedQuestion = data?.translatedQuestion;
  if (!translatedQuestion) {
    throw new Error(`Translated question ${questionId} not found`);
  }
  return toLegacyTranslatedQuestion(translatedQuestion);
};

export const fetchTranslatedSupplementGql = async (
  supplementId: string,
  language: AlternateContentLanguage,
  {includeDrafts = false, signal}: TranslationFetchOptions = {}
): Promise<TranslatedSupplement> => {
  const {data} = await client.query<
    GetTranslatedSupplementQuery,
    GetTranslatedSupplementQueryVariables
  >({
    query: GetTranslatedSupplementDocument,
    variables: {id: supplementId, language, includeDrafts},
    // See fetchTranslatedQuestionGql — same staleness/normalization hazards.
    fetchPolicy: 'no-cache',
    context: requestContext(signal)
  });

  const translatedSupplement = data?.translatedSupplement;
  if (!translatedSupplement) {
    throw new Error(`Translated supplement ${supplementId} not found`);
  }
  return toLegacyTranslatedSupplement(translatedSupplement);
};

export const createTranslationJobGql = async (
  language: AlternateContentLanguage,
  translationRequests: TranslationRequest[]
): Promise<TranslationJobStatus> => {
  const {data} = await client.mutate<
    CreateTranslationJobMutation,
    CreateTranslationJobMutationVariables
  >({
    mutation: CreateTranslationJobDocument,
    variables: {
      input: {
        language,
        // CreateTranslationJobInput.translationRequests is a JsonString list;
        // the server decodes each entry into the TranslationRequest embedded
        // resource, which keeps the legacy snake_case keys.
        translationRequests: translationRequests.map((request) => JSON.stringify(request))
      }
    }
  });

  const result = data?.createTranslationJob.result;
  if (!result) {
    throw new Error(
      toMutationErrorMessage(data?.createTranslationJob.errors, 'Failed to create translation job')
    );
  }
  return result;
};

export const getTranslationJobGql = async (jobId: string): Promise<TranslationJobStatus> => {
  const {data} = await client.query<GetTranslationJobQuery, GetTranslationJobQueryVariables>({
    query: GetTranslationJobDocument,
    variables: {id: jobId},
    // Job status is polled until it converges; a cached status never would.
    fetchPolicy: 'no-cache'
  });

  const job = data?.translationJob;
  if (!job) {
    throw new Error(`Translation job ${jobId} not found`);
  }
  return job;
};

const pollForJobCompletion = async (
  job: TranslationJobStatus,
  attempt: number = 0
): Promise<TranslationJobStatus> => {
  if (job.status === 'complete') {
    return job;
  }

  if (job.status === 'error') {
    throw new Error('Translation job failed');
  }

  const response = await getTranslationJobGql(job.id);

  if (response.status === 'complete') {
    return response;
  }

  // Poll every 2s for the first 5 attempts, then add 500ms for each additional attempt, maxing out at 8s
  const delay = Math.min(attempt < 5 ? 2000 : (attempt - 4) * 500 + 2000, 8000);
  await new Promise((resolve) => setTimeout(resolve, delay));

  return pollForJobCompletion(response, attempt + 1);
};

// NOTE THIS CAN THROW
export const runAndAwaitTranslationJob = async (
  language: AlternateContentLanguage,
  translationRequests: TranslationRequest[]
): Promise<void> => {
  const job = await createTranslationJobGql(language, translationRequests);
  await pollForJobCompletion(job);
};

export const upsertTranslatedField = async (
  parentType: 'question' | 'supplement',
  {language, resource_id: resourceId, field, notes, status, text}: TranslatedField
): Promise<void> => {
  const input = {resourceId, field, text, notes, status, language};

  if (parentType === 'question') {
    const {data} = await client.mutate<
      UpsertTranslatedQuestionFieldMutation,
      UpsertTranslatedQuestionFieldMutationVariables
    >({
      mutation: UpsertTranslatedQuestionFieldDocument,
      variables: {input}
    });

    if (data?.upsertTranslatedQuestionField.errors.length) {
      throw new Error(
        toMutationErrorMessage(
          data.upsertTranslatedQuestionField.errors,
          'Failed to save translated question field'
        )
      );
    }
    return;
  }

  const {data} = await client.mutate<
    UpsertTranslatedSupplementFieldMutation,
    UpsertTranslatedSupplementFieldMutationVariables
  >({
    mutation: UpsertTranslatedSupplementFieldDocument,
    variables: {input}
  });

  if (data?.upsertTranslatedSupplementField.errors.length) {
    throw new Error(
      toMutationErrorMessage(
        data.upsertTranslatedSupplementField.errors,
        'Failed to save translated supplement field'
      )
    );
  }
};
